import { useEffect, useState } from 'react';

import { Pagination } from '@/components/ui/pagination';

interface UsePaginationProps {
    totalElements: number;
    limit: number;
    /** Optional 1-based initial/controlled page for URL-driven pagination */
    page?: number;
    /** Called when the user navigates to a new page */
    onPageChange?: (page: number) => void;
}

interface PaginationControlProps {
    className?: string;
    layout?: 'centered' | 'inline';
}

interface UsePaginationReturn {
    limit: number;
    skip: number;
    Pagination: React.FC<PaginationControlProps>;
}

export function usePagination({
    totalElements,
    limit,
    page,
    onPageChange,
}: UsePaginationProps): UsePaginationReturn {
    const [currentPage, setCurrentPage] = useState(Math.max(1, page ?? 1));
    const totalPages = Math.ceil(totalElements / limit) || 1;

    // Sync internal page when the controlled `page` prop changes (browser
    // back/forward). A missing page means page 1, so going Back to a URL
    // without `?page` resets the table instead of leaving it where it was.
    useEffect(() => {
        setCurrentPage(Math.max(1, page ?? 1));
    }, [page]);

    // Clamp to the last page, except while a controlled page awaits totals
    useEffect(() => {
        if (page !== undefined && totalElements === 0) return;
        if (currentPage > totalPages) {
            setCurrentPage(totalPages > 0 ? totalPages : 1);
        }
    }, [totalPages, totalElements, page]);

    const handlePageChange = (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
        onPageChange?.(newPage);
    };

    const skip = (currentPage - 1) * limit;

    const PaginationComponent: React.FC<PaginationControlProps> = ({
        className,
        layout,
    }) => (
        <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className={className}
            layout={layout}
        />
    );

    return {
        limit,
        skip,
        Pagination: PaginationComponent,
    };
}
